import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  FaCheckCircle,
  FaTimesCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSpinner,
  FaTimes,
} from "react-icons/fa";

const Toast = ({
  id,
  type,
  message,
  duration,
  dismissible,
  onDismiss,
  action,
}) => {
  const timerRef = useRef(null);
  const startTimeRef = useRef(null);
  const remainingTimeRef = useRef(duration);
  const [isPaused, setIsPaused] = useState(false);

  // Get type-specific styling
  const getTypeStyles = () => {
    switch (type) {
      case "success":
        return {
          borderColor: "border-l-[#f6a332]",
          iconColor: "text-[#f6a332]",
          Icon: FaCheckCircle,
        };
      case "error":
        return {
          borderColor: "border-l-[#E31837]",
          iconColor: "text-[#E31837]",
          Icon: FaTimesCircle,
        };
      case "warning":
        return {
          borderColor: "border-l-[#f6a332]",
          iconColor: "text-[#f6a332]",
          Icon: FaExclamationTriangle,
        };
      case "info":
        return {
          borderColor: "border-l-[#505A64]",
          iconColor: "text-[#505A64]",
          Icon: FaInfoCircle,
        };
      case "loading":
        return {
          borderColor: "border-l-[#f6a332]",
          iconColor: "text-[#f6a332]",
          Icon: FaSpinner,
        };
      default:
        return {
          borderColor: "border-l-[#505A64]",
          iconColor: "text-[#505A64]",
          Icon: FaInfoCircle,
        };
    }
  };

  const { borderColor, iconColor, Icon } = getTypeStyles();

  // Auto-dismiss timer logic
  useEffect(() => {
    if (duration > 0) {
      startTimeRef.current = Date.now();
      remainingTimeRef.current = duration;

      const startTimer = () => {
        timerRef.current = setTimeout(() => {
          onDismiss(id);
        }, remainingTimeRef.current);
      };

      startTimer();

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }
  }, [duration, id, onDismiss]);

  // Handle hover pause/resume
  const handleMouseEnter = () => {
    if (duration > 0 && timerRef.current) {
      setIsPaused(true);
      clearTimeout(timerRef.current);
      const elapsed = Date.now() - startTimeRef.current;
      remainingTimeRef.current = remainingTimeRef.current - elapsed;
    }
  };

  const handleMouseLeave = () => {
    if (duration > 0 && isPaused) {
      setIsPaused(false);
      startTimeRef.current = Date.now();
      timerRef.current = setTimeout(() => {
        onDismiss(id);
      }, remainingTimeRef.current);
    }
  };

  // Handle dismiss button click
  const handleDismiss = () => {
    onDismiss(id);
  };

  // Animation variants
  const toastVariants = {
    initial: {
      opacity: 0,
      y: -20,
      scale: 0.95,
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.3,
        ease: [0.4, 0, 0.2, 1],
      },
    },
    exit: {
      opacity: 0,
      y: -20,
      scale: 0.95,
      transition: {
        duration: 0.2,
        ease: [0.4, 0, 1, 1],
      },
    },
  };

  // ARIA attributes
  const ariaRole = type === "error" || type === "warning" ? "alert" : "status";
  const ariaLive =
    type === "error" || type === "warning" ? "assertive" : "polite";

  return (
    <motion.div
      variants={toastVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      role={ariaRole}
      aria-live={ariaLive}
      aria-atomic="true"
      className={`
        relative
        flex
        items-start
        gap-3
        min-w-[320px]
        max-w-[500px]
        bg-white
        rounded-[10px]
        shadow-[0_4px_12px_rgba(0,0,0,0.15)]
        p-4
        border-l-4
        ${borderColor}
        pointer-events-auto
      `}
    >
      {/* Icon */}
      <div className={`flex-shrink-0 ${iconColor} text-xl mt-0.5`}>
        {type === "loading" ? <Icon className="animate-spin" /> : <Icon />}
      </div>

      {/* Message and Action */}
      <div className="flex-1 text-[#222222] text-sm leading-relaxed pr-2">
        <div className="mb-1">{message}</div>
        {action && (
          <button
            onClick={action.onClick}
            className="text-xs font-medium text-blue-600 hover:text-blue-800 underline focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
          >
            {action.label}
          </button>
        )}
      </div>

      {/* Dismiss button */}
      {dismissible && (
        <button
          onClick={handleDismiss}
          aria-label="Dismiss notification"
          className="
            flex-shrink-0
            text-[#505A64]
            hover:text-[#222222]
            transition-colors
            duration-200
            p-1
            -mt-1
            -mr-1
            focus:outline-none
            focus:ring-2
            focus:ring-[#f6a332]
            focus:ring-offset-2
            rounded
          "
        >
          <FaTimes className="text-sm" />
        </button>
      )}
    </motion.div>
  );
};

export default React.memo(Toast);
